import { exportPdf, exportPng, exportSvg, ExportTooLargeError, type ExportOptions } from '@vp/render';
import { CoreTypes } from '@vp/shared-types';
import { live } from './editor/bridge';
import { logger, storage } from './platform';
import { isObjectUnavailable } from './modules';
import { t } from './i18n';
import { embeddedFontCss, ensureFontsFor, pdfFontFaces } from './fonts';

export function download(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

export function safeFileName(s: string): string {
  return (s.replace(/[\\/:*?"<>|]+/g, '_').trim() || 'canvas').slice(0, 100);
}

async function blobToDataUrl(b: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(b);
  });
}

/** What to export; `frame:<id>` = one frame with its contents, cropped exactly to the frame; `frames` = each frame on its own PDF page. */
export type ExportScope = 'selection' | 'view' | 'all' | 'frames' | `frame:${string}`;

function frameOfScope(scope: ExportScope) {
  if (!scope.startsWith('frame:')) return null;
  const o = live.editor?.doc.get(scope.slice(6));
  return o && o.type === CoreTypes.frame ? o : null;
}

export interface ImageExportRequest {
  format: 'png' | 'svg' | 'pdf';
  scope: ExportScope;
  scale: 1 | 2 | 3;
  background: string | null;
  unavailable: 'placeholder' | 'skip';
}

function baseOptions(req: ImageExportRequest): ExportOptions | null {
  const ed = live.editor;
  const r = live.renderer;
  if (!ed || !r) return null;
  const frame = frameOfScope(req.scope);
  const ids = frame ? [frame.id] : req.scope === 'selection' ? ed.selection : null;
  const view = req.scope === 'view';
  const bounds = frame ? { x: frame.x, y: frame.y, w: frame.w, h: frame.h } : view ? r.camera.visibleRect() : undefined;
  // the visible area comes out the size it is on screen; elsewhere 1 world unit = 1 px (PNG times its scale)
  const zoom = view ? r.camera.zoom : 1;
  return {
    ids: ids && ids.length ? ids : null,
    bounds,
    padding: view || frame ? 0 : 16,
    scale: (req.format === 'png' ? req.scale : 1) * zoom,
    background: req.background,
    unavailable: req.unavailable,
    isUnavailable: isObjectUnavailable,
    images: r.opts.images,
    imageDataUrl: async (src) => {
      const blob = await storage.getAttachment(src.startsWith('att:') ? src.slice(4) : src);
      return blob ? blobToDataUrl(blob) : null;
    },
    fontCss: embeddedFontCss,
  };
}

/** Export image; returns an error message or null. */
export async function exportImage(req: ImageExportRequest): Promise<string | null> {
  const opts = baseOptions(req);
  const ed = live.editor;
  if (!opts || !ed) return null;
  const frame = frameOfScope(req.scope);
  const base = live.session?.meta.value.name ?? 'canvas';
  const name = safeFileName(frame ? `${base} - ${(frame.props['title'] as string) || t('props.frameUntitled')}` : base);
  const t0 = performance.now();
  try {
    await ensureFontsFor(ed.doc.allObjects());
    if (req.format === 'png') download(await exportPng(ed.scene, opts), `${name}.png`);
    else if (req.format === 'pdf') {
      const pages = req.scope === 'frames' ? framePages(opts) : [opts];
      const bytes = await exportPdf(ed.scene, pages, { title: base, fontFaces: pdfFontFaces });
      download(new Blob([bytes as BlobPart], { type: 'application/pdf' }), `${name}.pdf`);
    } else download(new Blob([await exportSvg(ed.scene, opts)], { type: 'image/svg+xml' }), `${name}.svg`);
    logger.info('export.image', { format: req.format, scope: frame ? 'frame' : req.scope, ms: Math.round(performance.now() - t0) });
    return null;
  } catch (e) {
    if (e instanceof ExportTooLargeError) return t('export.tooLarge', { w: e.width, h: e.height });
    logger.error('export.image', { format: req.format, error: e instanceof Error ? e.name : 'unknown' });
    return e instanceof Error ? e.message : String(e);
  }
}

/** Every frame as an area of its own, in reading order (top to bottom, left to right). */
function framePages(base: ExportOptions): ExportOptions[] {
  const ed = live.editor;
  if (!ed) return [];
  const frames = [...ed.doc.allObjects()].filter((o) => o.type === CoreTypes.frame).sort((a, b) => a.y - b.y || a.x - b.x);
  return frames.map((f) => ({ ...base, ids: [f.id], bounds: { x: f.x, y: f.y, w: f.w, h: f.h }, padding: 0 }));
}

/** Copy the selection to the system clipboard as PNG (§4.6). */
export async function copySelectionAsPng(notify: (level: 'info' | 'warning' | 'error', m: string) => void): Promise<void> {
  const req: ImageExportRequest = { format: 'png', scope: 'selection', scale: 2, background: null, unavailable: 'placeholder' };
  const opts = baseOptions(req);
  const ed = live.editor;
  if (!opts || !ed) return;
  try {
    const png = exportPng(ed.scene, opts);
    // Promise-valued items keep the user activation in Chromium
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': png })]);
    notify('info', t('clipboard.copied'));
  } catch (e) {
    notify('warning', e instanceof ExportTooLargeError ? t('export.tooLarge', { w: e.width, h: e.height }) : t('clipboard.pngFailed'));
  }
}
